import type { MetaAdDraftInput } from '../../types/metaAdDraft';

// Stage 7A D1 — pure at-a-glance pre-publish checklist. NOT the publish gate
// (that's validateMetaAdDraft); this is a human-readable "what's filled in"
// summary. Each item is done|not-done with an optional short detail. Pure so the
// Preview panel re-derives it live + it's unit-testable.

export type ChecklistItem = {
  key: string;
  label: string;
  done: boolean;
  detail?: string;
};

function nonEmpty(s: string | null | undefined): boolean {
  return typeof s === 'string' && s.trim().length > 0;
}

function countNonEmpty(arr: string[] | undefined): number {
  return (arr ?? []).filter((s) => nonEmpty(s)).length;
}

export function buildPrePublishChecklist(input: MetaAdDraftInput): ChecklistItem[] {
  const { campaign, adSets, ads } = input;
  const cbo = campaign.budgetMode === 'CBO';
  // Budget done: CBO → the campaign budget; ABO → every ad set carries one.
  const budgetDone = cbo
    ? Boolean(campaign.budget && campaign.budget.amountCents > 0)
    : adSets.length > 0 && adSets.every((a) => a.budget != null && a.budget.amountCents > 0);
  // Audience done: every ad set is extracted OR has a geo layer.
  const audienceDone =
    adSets.length > 0 &&
    adSets.every((a) => {
      const t = a.targeting;
      return (
        t.sourceType === 'extracted' ||
        t.countries.length > 0 ||
        t.cities.length > 0 ||
        t.regions.length > 0
      );
    });
  const anyOverride = adSets.some((a) => a.datesOverridden);
  const creative = ads.creative;
  // D3.4: the Feed asset (image or video) is the required creative.
  const hasAsset = nonEmpty(creative.feedAsset.url) && nonEmpty(creative.feedAsset.path);
  const mc = campaign.placements.manualConfig;
  const placementsSet =
    campaign.placements.mode === 'automatic' ||
    (mc != null &&
      mc.facebookPositions.length +
        mc.instagramPositions.length +
        mc.messengerPositions.length +
        mc.audienceNetworkPositions.length >
        0);
  const dest = ads.destination;
  const destinationSet =
    dest.type === 'instant_form' ? nonEmpty(dest.leadGenFormId) : nonEmpty(dest.url);

  const headlineN = countNonEmpty(creative.headlines);
  const primaryN = countNonEmpty(creative.primaryTexts);
  const descriptionN = countNonEmpty(creative.descriptions);
  const adSetDetail = adSets.length === 1 ? '1 ad set' : `${adSets.length} ad sets`;

  return [
    { key: 'account', label: 'Account selected', done: nonEmpty(input.accountId) },
    {
      key: 'goal',
      label: 'Goal set',
      done: true,
      detail: campaign.objective === 'LEADS' ? 'Leads' : 'Traffic',
    },
    {
      key: 'adsets',
      label: 'Ad sets',
      done: adSets.length > 0,
      detail: adSetDetail,
    },
    {
      key: 'budget',
      label: 'Budget configured',
      done: budgetDone,
      detail: cbo ? 'Campaign (CBO)' : 'Ad set (ABO)',
    },
    {
      key: 'schedule',
      label: 'Schedule set',
      done: true,
      detail: nonEmpty(campaign.defaultStartDate)
        ? 'Scheduled start'
        : anyOverride
          ? 'Per-ad-set override'
          : 'Will go live on publish',
    },
    {
      key: 'audience',
      label: 'Audience configured',
      done: audienceDone,
      detail: adSetDetail,
    },
    {
      key: 'placements',
      label: 'Placements set',
      done: placementsSet,
      detail: campaign.placements.mode === 'automatic' ? 'Automatic' : 'Manual',
    },
    { key: 'page', label: 'Page selected', done: nonEmpty(campaign.facebookPageId) },
    {
      key: 'destination',
      label: 'Destination set',
      done: destinationSet,
      detail: dest.type === 'instant_form' ? 'Lead form' : 'Website URL',
    },
    {
      key: 'creative',
      label: 'Feed creative uploaded',
      done: hasAsset,
      detail: creative.assetType === 'video' ? 'Video' : 'Image',
    },
    {
      key: 'headlines',
      label: 'Headlines',
      done: headlineN >= 1,
      detail: `${headlineN} added`,
    },
    {
      key: 'primaryTexts',
      label: 'Primary text',
      done: primaryN >= 1,
      detail: `${primaryN} added`,
    },
    {
      key: 'descriptions',
      label: 'Descriptions',
      done: true,
      detail: `${descriptionN} added`,
    },
    { key: 'cta', label: 'CTA selected', done: nonEmpty(creative.callToAction) },
    { key: 'pixel', label: 'Pixel selected', done: nonEmpty(ads.pixelId) },
  ];
}
